import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { X, Calculator, Sparkles } from "lucide-react";

/**
 * Safe formula evaluator.
 *
 * We accept a JS-flavoured expression (e.g. "cover_amount * 0.0015 + (smoker ? 50 : 0)")
 * and evaluate it in a sandbox where only the calculator's own field names
 * plus a whitelisted `Math` are available. The Function constructor is used
 * inside a `with({})` shim to prevent globals from leaking.
 */
function safeEval(expr, ctx) {
    if (!expr || typeof expr !== "string") return null;
    // Strip anything that looks like a statement / global / dangerous keyword
    // as a defence-in-depth measure — the sandbox already blocks globals but
    // this keeps unfriendly expressions out of the evaluator entirely.
    if (/(=[^=]|=>|;|window|document|import|require|eval|Function|process|fetch|XMLHttpRequest|constructor|prototype|__proto__)/.test(
        expr
    )) {
        throw new Error("Unsafe expression");
    }
    const keys = Object.keys(ctx || {});
    const args = keys.concat(["Math"]);
    const vals = keys.map((k) => ctx[k]).concat([Math]);
    const fn = new Function(...args, `"use strict"; return (${expr});`);
    return fn(...vals);
}

const CURRENCY_FMT = (n, cur = "HKD") =>
    Number.isFinite(n)
        ? `${cur} ${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`
        : "—";

/**
 * CalculatorModal — full-screen, brand-consistent quote calculator.
 *
 * Props:
 *   open       : boolean
 *   onClose()  : close handler
 *   config     : { title?, description?, currency?, fields:[{name,label,type,min,max,options?,required?}], formula? }
 *   deal       : { title, vendor }
 *   onSubmit(inputs, quote) : called when member confirms the quote
 *   busy       : disable submit while true
 */
export default function CalculatorModal({
    open,
    onClose,
    config,
    deal,
    onSubmit,
    busy = false,
}) {
    const [values, setValues] = useState(() =>
        (config?.fields || []).reduce((acc, f) => {
            acc[f.name] = f.type === "checkbox" ? false : "";
            return acc;
        }, {})
    );
    const [formulaError, setFormulaError] = useState(null);

    const currency = (config?.currency || "HKD").toUpperCase();

    const inputsForFormula = useMemo(() => {
        // Coerce numeric strings so formula math works
        const out = {};
        for (const f of config?.fields || []) {
            let v = values[f.name];
            if (f.type === "number") {
                v = v === "" || v == null ? 0 : Number(v);
                if (!Number.isFinite(v)) v = 0;
            }
            out[f.name] = v;
        }
        return out;
    }, [values, config]);

    const quote = useMemo(() => {
        if (!config?.formula) return null;
        try {
            const q = safeEval(config.formula, inputsForFormula);
            setFormulaError(null);
            return typeof q === "number" && Number.isFinite(q) ? q : null;
        } catch (e) {
            setFormulaError(e.message);
            return null;
        }
    }, [config, inputsForFormula]);

    const missingRequired = (config?.fields || []).some((f) => {
        if (!f.required) return false;
        const v = values[f.name];
        if (f.type === "checkbox") return false;
        return v === "" || v == null;
    });

    if (!open) return null;

    return (
        <div
            data-testid="calculator-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[70] bg-navy-900/70 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto"
        >
            <div className="w-full min-h-full lg:my-8 lg:max-w-5xl bg-cream lg:rounded-3xl shadow-lift flex flex-col">
                {/* Header */}
                <header className="bg-navy-700 text-white lg:rounded-t-3xl px-8 lg:px-12 py-6 flex items-start justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-2">
                            Indicative quote
                        </div>
                        <h2 className="font-display text-3xl lg:text-4xl leading-tight">
                            {config?.title || `${deal?.title} calculator`}
                        </h2>
                        {(config?.description || deal?.vendor) && (
                            <p className="text-navy-50/70 text-sm mt-2 max-w-2xl">
                                {config?.description ||
                                    `Estimate your premium with ${deal.vendor}. This is indicative — the vendor will confirm.`}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="calc-close"
                        className="p-2 rounded-full hover:bg-white/10 text-navy-50/80 hover:text-white"
                        aria-label="Close calculator"
                    >
                        <X size={22} />
                    </button>
                </header>

                {/* Body */}
                <div className="flex-1 grid lg:grid-cols-[1.4fr_1fr] gap-8 px-8 lg:px-12 py-10">
                    <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-600 mb-4">
                            <Calculator size={14} /> Your details
                        </div>
                        <div className="grid gap-5 sm:grid-cols-2">
                            {(config?.fields || []).map((f) => (
                                <CalcField
                                    key={f.name}
                                    field={f}
                                    value={values[f.name]}
                                    onChange={(v) =>
                                        setValues((s) => ({ ...s, [f.name]: v }))
                                    }
                                />
                            ))}
                        </div>
                        {formulaError && (
                            <div
                                data-testid="calc-formula-error"
                                className="mt-6 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                Calculator formula error: {formulaError}
                            </div>
                        )}
                    </div>

                    {/* Quote summary */}
                    <aside className="lg:pl-6 lg:border-l lg:border-navy-100">
                        <div className="sticky top-8 space-y-6">
                            <div className="rounded-2xl bg-white shadow-soft p-6">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gold-600 mb-1">
                                    <Sparkles size={12} /> Indicative price
                                </div>
                                <div className="font-display text-5xl text-navy-700 leading-none mt-3">
                                    {quote != null
                                        ? CURRENCY_FMT(quote, currency)
                                        : (
                                            <span className="text-navy-300 text-2xl">
                                                Fill in your details
                                            </span>
                                        )}
                                </div>
                                <p className="text-xs text-navy-500 mt-4 leading-relaxed">
                                    This is a rough estimate based on your inputs.{" "}
                                    {deal?.vendor || "The vendor"} will confirm the final
                                    price and any exclusions when they pick up your case.
                                </p>
                            </div>

                            <Button
                                type="button"
                                onClick={() => onSubmit?.(inputsForFormula, quote)}
                                data-testid="calc-submit"
                                disabled={busy || missingRequired}
                                className="w-full h-12 rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold disabled:opacity-50"
                            >
                                {busy
                                    ? "Sending to vendor…"
                                    : "Send my details to the vendor"}
                            </Button>
                            <p className="text-[11px] text-navy-500 text-center">
                                A case will be raised with the vendor including your inputs
                                above. They&apos;ll follow up directly.
                            </p>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function CalcField({ field, value, onChange }) {
    const { name, label, type, min, max, options, required } = field;
    const testId = `calc-field-${name}`;
    if (type === "checkbox") {
        return (
            <label
                htmlFor={testId}
                className="flex items-center gap-3 rounded-xl border border-navy-100 bg-white px-4 py-3 cursor-pointer sm:col-span-2"
            >
                <Checkbox
                    id={testId}
                    data-testid={testId}
                    checked={Boolean(value)}
                    onCheckedChange={(v) => onChange(Boolean(v))}
                />
                <span className="text-sm text-navy-700">
                    {label}
                    {required && <span className="text-gold-600 ml-1">*</span>}
                </span>
            </label>
        );
    }
    if (type === "select" && Array.isArray(options)) {
        return (
            <div>
                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                    {label}
                    {required && <span className="text-gold-600 ml-1">*</span>}
                </Label>
                <Select value={String(value ?? "")} onValueChange={onChange}>
                    <SelectTrigger data-testid={testId} className="mt-2 h-11 bg-white border-navy-100">
                        <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((o) => {
                            const val =
                                typeof o === "object" ? String(o.value) : String(o);
                            const lbl = typeof o === "object" ? o.label : String(o);
                            return (
                                <SelectItem key={val} value={val}>
                                    {lbl}
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            </div>
        );
    }
    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <Input
                type={type === "number" ? "number" : "text"}
                min={min}
                max={max}
                required={required}
                data-testid={testId}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 h-11 bg-white border-navy-100 focus-visible:ring-gold-500"
            />
        </div>
    );
}
